import styled from "@emotion/styled";
import classNames from "classnames";
import { useCallback, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";

import {
  beginLearningKeyboardLayout,
  getKeyCap,
  isModifierKey,
  type KeyCombination,
} from "@skilitics/interact-keyboard";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { IconButton } from "../../buttons/Buttons.tsx";
import { SkiliticsIcon } from "../../icons/Icon.tsx";
import { KeyChord } from "./KeyChord.tsx";

// begin learning the keyboard layout when module loads
beginLearningKeyboardLayout();

const TRANSITION = "300ms ease-in-out";

const Container = styled.div`
  display: flex;
  align-items: center;

  width: 100%;

  color: ${({ theme }) => theme.generalTextIconVariant};
`;

const Placeholder = styled.span`
  position: absolute;

  transition: opacity ${TRANSITION};
`;

const Instructions = styled.span`
  position: absolute;

  color: ${({ theme }) => theme.primaryStrong};

  opacity: 0;
  transition: opacity ${TRANSITION};
`;

const RemoveIconButton = styled(IconButton)`
  margin-inline-start: auto;
`;

const CaptureContainer = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  height: 30px;
  width: 100%;

  background: ${({ theme }) => theme.surfaceVariant};

  padding: 4px 9px;
  border: 1px solid ${({ theme }) => theme.outlineSuperSoft};
  border-radius: 5px;

  font-size: ${({ theme }) => theme.fontSizeSmall};

  cursor: pointer;

  overflow: hidden;

  transition: border-color ${TRANSITION};

  &.value {
    justify-content: flex-start;
    padding-inline-end: 0;

    color: ${({ theme }) => theme.generalTextIcon};
  }

  &:hover {
    border-color: ${({ theme }) => theme.outlineMedium};
  }

  &:focus {
    border-color: ${({ theme }) => theme.primary};

    ${Placeholder} {
      opacity: 0;
    }

    ${Instructions} {
      opacity: 1;
    }
  }
`;

export type KeyCaptureProps = {
  value?: KeyCombination;
  onChange: (combination?: KeyCombination) => void;
  disabled?: boolean;
};

export const KeyCapture = ({ value, onChange, disabled = false }: KeyCaptureProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const { code, ctrlKey, shiftKey, altKey, metaKey, key } = event;

      // Skip pure modifier key presses (wait for actual key)
      if (isModifierKey(code)) return;

      // Get the base key-cap character from the Keyboard Layout API or learned cache
      // This allows us to show the correct character for non-QWERTY layouts
      void getKeyCap(code, key, altKey).then((keyCap) =>
        onChange({ code, key, keyCap: keyCap ?? key, ctrlKey, shiftKey, altKey, metaKey }),
      );
    },
    [onChange],
  );

  return (
    <Container>
      <CaptureContainer
        ref={containerRef}
        tabIndex={0}
        className={classNames({ disabled, value })}
        onKeyDown={handleKeyDown}
      >
        {value ? (
          <>
            <KeyChord keyCombination={value} />
            <RemoveIconButton onClick={() => onChange(undefined)} data-test="remove-key-press-combination">
              <SkiliticsIcon icon="cross" size={16} />
            </RemoveIconButton>
          </>
        ) : (
          <>
            <Placeholder>{formatMessage("placeholder")}</Placeholder>
            <Instructions>{formatMessage("instructions")}</Instructions>
          </>
        )}
      </CaptureContainer>
    </Container>
  );
};

const formatStrings = {
  placeholder: {
    en: "Click to focus & press key",
  },
  instructions: {
    en: "Press key or key combo",
  },
} satisfies FormatMessages;
